import React, { useMemo } from 'react';
import useStore from '../store';

function parseTime(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  return h + m / 60;
}

export default function CalcScreen() {
  const banya = useStore((s) => s.banya);

  // Пастельная палитра цветов для участников
  const colors = [
    { bg: '#ff8a80' },
    { bg: '#82b1ff' },
    { bg: '#b9f6ca' },
    { bg: '#ffd740' },
    { bg: '#e0b0ff' },
    { bg: '#80deea' },
    { bg: '#ffab91' },
    { bg: '#aed581' },
    { bg: '#f48fb1' },
    { bg: '#9fa8da' },
  ];

  const calculation = useMemo(() => {
    if (!banya) return null;

    const { startTime, endTime, hourlyRate, broomCost, people } = banya;

    const start = parseTime(startTime);
    let end = parseTime(endTime);
    if (end <= start) end += 24;

    const totalHours = end - start;
    if (totalHours <= 0) return null;

    const totalBanyaCost = hourlyRate * totalHours;
    const totalCost = totalBanyaCost + broomCost;
    const costPerHour = totalCost / totalHours;

    const personTimes = people.map((p) => {
      let a = parseTime(p.arrivedAt);
      let l = parseTime(p.leftAt);
      if (l <= a) l += 24;
      if (a < start) a = start;
      if (l > end) l = end;
      return { ...p, arrivedAtNum: a, leftAtNum: l };
    });

    const events = [];
    events.push({ time: start, type: 'start' });
    events.push({ time: end, type: 'end' });
    personTimes.forEach((p) => {
      events.push({ time: p.arrivedAtNum, type: 'arrive', personId: p.id });
      events.push({ time: p.leftAtNum, type: 'leave', personId: p.id });
    });
    events.sort((a, b) => a.time - b.time);

    const banks = [];
    let currentPeople = [];
    let currentTime = start;

    for (const event of events) {
      if (event.time === currentTime) {
        if (event.type === 'arrive') {
          const p = personTimes.find((x) => x.id === event.personId);
          if (p) currentPeople.push(p);
        } else if (event.type === 'leave') {
          currentPeople = currentPeople.filter((x) => x.id !== event.personId);
        }
        continue;
      }

      if (event.time > currentTime) {
        const duration = event.time - currentTime;
        if (currentPeople.length > 0) {
          banks.push({
            from: currentTime,
            to: event.time,
            duration,
            people: [...currentPeople],
          });
        }
        currentTime = event.time;
      }

      if (event.type === 'arrive') {
        const p = personTimes.find((x) => x.id === event.personId);
        if (p) currentPeople.push(p);
      } else if (event.type === 'leave') {
        currentPeople = currentPeople.filter((x) => x.id !== event.personId);
      }
    }

    const personTotals = {};
    personTimes.forEach((p) => {
      personTotals[p.id] = { name: p.name, emoji: p.emoji, total: 0 };
    });

    banks.forEach((bank) => {
      const bankCost = costPerHour * bank.duration;
      const share = bankCost / bank.people.length;
      bank.people.forEach((p) => {
        personTotals[p.id].total += share;
      });
    });

    const totalCheck = Object.values(personTotals).reduce((sum, p) => sum + p.total, 0);

    const formatTime = (hours) => {
      const h = Math.floor(hours);
      const m = Math.round((hours - h) * 60);
      if (h >= 24) return `${String(h - 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    };

    const formatDuration = (hours) => {
      if (hours < 1) return `${Math.round(hours * 60)}мин`;
      const h = Math.floor(hours);
      const m = Math.round((hours - h) * 60);
      if (m === 0) return `${h}ч`;
      return `${h}ч ${m}мин`;
    };

    return {
      startTime, endTime, hourlyRate, broomCost, people,
      totalBanyaCost, totalCost, totalHours, costPerHour,
      banks, personTotals: Object.values(personTotals), totalCheck,
      personTimes,
      formatTime, formatDuration,
    };
  }, [banya]);

  if (!calculation) {
    return (
      <div className="calc-section">
        {banya ? (
          <div className="calc-card"><p>Некорректное время бани</p></div>
        ) : (
          <div className="calc-card"><p>Нет данных</p></div>
        )}
      </div>
    );
  }

  const {
    formatTime, formatDuration, totalBanyaCost, totalCost, totalHours,
    costPerHour, banks, personTotals, totalCheck, people, personTimes,
    startTime, endTime, hourlyRate, broomCost
  } = calculation;

  const fmt = (n) => n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="calc-section">
      <div className="calc-card">
        <h3>Сводка</h3>
        {personTotals.map((p, idx) => {
          const personCalc = calculation.personTimes.find(pt => pt.name === p.name);
          const c = colors[idx % colors.length];
          return (
            <div className="person-result" key={p.name}>
              <div className="person-row-top">
                <span className="stats-name-label" style={{ backgroundColor: c.bg }}><span className="stats-name-label-inner"><span className="emoji-inline">{p.emoji || '😎'}</span><span>{p.name}</span></span></span>
              </div>
              <div className="person-row-bottom">
                <span className="person-time-col"><span className="person-time">{personCalc ? personCalc.arrivedAt + '—' + personCalc.leftAt : ''}</span></span>
                <span className="person-duration-col"><span className="person-duration">{personCalc ? formatDuration(personCalc.leftAtNum - personCalc.arrivedAtNum) : ''}</span></span>
                <span className="person-amount-col"><span className="amount">{fmt(p.total)} ₽</span></span>
              </div>
            </div>
          );
        })}
        <div className="row total mt-8">
          <span>Всего денег:</span><span>{fmt(totalCheck)} ₽</span>
        </div>
        <div className="row total total-time"><span>Общее время:</span><span className="mono">{startTime}—{endTime} ч</span></div>
      </div>

      <div className="calc-card">
        <h3>Деньги</h3>
        <div className="row"><span className="label">Стоимость часа</span><span>{hourlyRate.toLocaleString('ru-RU')} ₽</span></div>
        <div className="row">
          <span className="label">Стоимость бани</span>
          <span>{hourlyRate.toLocaleString('ru-RU')} ₽ × {formatDuration(totalHours)} = {totalBanyaCost.toLocaleString('ru-RU')} ₽</span>
        </div>
        <div className="row"><span className="label">Стоимость веника</span><span>{broomCost.toLocaleString('ru-RU')} ₽</span></div>
        <div className="row total"><span>Общий расход</span><span>{totalCost.toLocaleString('ru-RU')} ₽</span></div>
        <div className="row"><span className="label">Расход в час</span><span>{fmt(costPerHour)} ₽</span></div>
      </div>


      <div className="calc-card">
        <h3>Расчёт</h3>
        {banks.map((bank, idx) => (
          <div className="bank-section" key={idx}>
            <div className="bank-title-row">
              <h4>Банк {idx + 1}</h4>
              <div className="bank-header"><span>{formatDuration(bank.duration)}</span><span className="separator-dot" /><span>{formatTime(bank.from)}—{formatTime(bank.to)}</span></div>
            </div>
            <div className="bank-item">
              <div className="bank-people">
                {bank.people.map((p, pi, arr) => {
                  const pc = colors[people.findIndex(pp => pp.id === p.id) % colors.length];
                  return (
                    <span key={p.id} className="inline-flex-center">
                      <span className="badge-name-sm" style={{ backgroundColor: pc.bg }}><span className="emoji-inline">{p.emoji || '😎'}</span>{p.name}</span>
                    </span>
                  );
                })}
              </div>
              <div className="bank-calculation">
                {fmt(costPerHour)} ₽ × {formatDuration(bank.duration)} / {bank.people.length}чел = <strong>{fmt((costPerHour * bank.duration) / bank.people.length)} ₽</strong>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
